import { Box, Paper, Stack, Typography } from '@mui/material';

import VnModalBody from '@/modals/components/VnModalBody';
import VnModalFooter from '@/modals/components/VnModalFooter';

export type AnalyticsTooltipItem = {
  title: string;
  desc: string;
};

export const ANALYTICS_TOOLTIPS = {
  appAnalytics: {
    key: 'appAnalytics',
    items: [
      {
        title: 'Unique Users',
        desc: 'Page Views by unique viewers in the time period.',
      },
      {
        title: 'Page Views',
        desc: 'Total number of pages viewed for the period set in the dashboard.',
      },
      {
        title: 'Video Views',
        desc: 'Total number of individual assets viewed partially or by its entirety for the period set in the dashboard.',
      },
      {
        title: 'Watch Time',
        desc: 'Aggregate total number of hours of assets viewed for the period set in the dashboard.',
      },
    ],
  },

  allTimeStats: {
    key: 'allTimeStats',
    items: [
      {
        title: 'Total Assets',
        desc: 'Total number of individual assets ingested.',
      },
      {
        title: 'Total Storage',
        desc: 'Total GB used to store the assets ingested.',
      },
      {
        title: 'Total Hours',
        desc: 'Total number of hours of assets ingested.',
      },
      {
        title: 'Total Channels',
        desc: 'Total number of channels created.',
      },
      {
        title: 'Total Live Content',
        desc: 'Total number of hours of live content ingested.',
      },
    ],
  },

  charts: {
    assetCountAnalytics: {
      items: [
        {
          title: 'Asset Count',
          desc: 'The total number of individual assets ingested.',
        },
        {
          title: 'Storage Size',
          desc: 'Total GB used to store the assets ingested.',
        },
        {
          title: 'Stored Hours',
          desc: 'Total number of hours stored in the library.',
        },
        {
          title: 'Ingested Hours',
          desc: 'Total number of hours of assets ingested.',
        },
      ],
    },
    assetCount: {
      items: [
        {
          title: '',
          desc: 'The total number of individual assets ingested.',
        },
      ],
    },
    storageSize: {
      items: [
        {
          title: '',
          desc: 'Total GB used to store the assets ingested',
        },
      ],
    },
    storedHours: {
      items: [
        {
          title: '',
          desc: 'Total number of hours stored in the library.',
        },
      ],
    },
    ingestedHours: {
      items: [
        {
          title: '',
          desc: 'Total number of hours of assets ingested.',
        },
      ],
    },
    liveContents: {
      items: [
        { title: 'Live Program Streams', desc: ' Number of live programs that have streamed.' },
        { title: 'Live Content Hours', desc: ' Total number of hours of live content ingested.' },
      ],
    },
    allJobsByType: {
      items: [
        { title: 'Sub Clips', desc: 'Total number of sub clips created.' },
        {
          title: 'Conform',
          desc: ' Number of jobs to prepare the video elements to playout correctly everywhere it will be used.',
        },
        {
          title: 'Speechtotext',
          desc: ' Total number of timed text files created.',
        },
        {
          title: 'Thumbnail',
          desc: ' Total number of thumbnails created.',
        },
      ],
    },
    confirmJobsByType: {
      items: [
        {
          title: '',
          desc: 'Number of jobs to prepare the video elements to playout correctly everywhere it will be used.',
        },
      ],
    },
    confirmJobsByStatus: {
      items: [
        {
          title: '',
          desc: 'Number of jobs to prepare the video elements to playout correctly everywhere it will be used.',
        },
      ],
    },
  },
} as const;

type AnalyticsTooltipProps = {
  tooltipItems: AnalyticsTooltipItem[];
  chartKey: string;
};

const AnalyticsTooltip = ({ tooltipItems, chartKey }: AnalyticsTooltipProps) => {
  // Resolve content by key
  const content: AnalyticsTooltipItem[] =
    tooltipItems || ANALYTICS_TOOLTIPS.charts[chartKey]?.items || ANALYTICS_TOOLTIPS[chartKey]?.items;

  const items = content ?? [];

  if (!items.length) {
    return null; // or fallback UI
  }

  return (
    <>
      {/* This uses info-tooltip class from _theme_base.scss */}
      <VnModalBody>
        <Box className="info-tooltip">
          <Stack spacing={1}>
            {items.map((item: AnalyticsTooltipItem) => (
              <Paper key={item.title} elevation={0} className="info-tooltip-item">
                {item.title !== '' && (
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: 12.5,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.title}:
                  </Typography>
                )}

                <Typography
                  sx={{
                    fontSize: 12,
                    lineHeight: 1.4,
                  }}
                >
                  {item.desc}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Box>
      </VnModalBody>

      <VnModalFooter right={<div />} />
    </>
  );
};

export default AnalyticsTooltip;
